/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { memo, useEffect, useMemo, useRef, useState } from "react";
import type { FunctionComponent, JSX, MutableRefObject } from "react";

import { Log } from "../../../model/logging";
import type { ImageLoaderProps } from "./ImageLoaderProps";
import { ImageLoadState } from "./ImageLoaderTypes";

import "./ImageLoader.scss";

/**
 * Image loader - Optimized, Robust image handling with
 * built-in loading and error states.
 */
const ImageLoaderComponent: FunctionComponent<ImageLoaderProps> = (
  props: ImageLoaderProps
): JSX.Element => {
  const TAG: string = "ImageLoader";

  const {
    src,
    fallbackSrc,
    fallbackText = "",
    classStyle = "",
    dataTestId,
    disableAnimation = false,
    onLoadComplete
  } = props;

  const [loadState, setLoadState] = useState<ImageLoadState>(
    ImageLoadState.IDLE
  );
  // Tracks whether the fallback image URL is currently being displayed
  const [usingFallbackSrc, setUsingFallbackSrc] = useState<boolean>(false);
  const imageRef: MutableRefObject<HTMLImageElement | null> = useRef(null);
  // Ref mirrors fallbackSrc so the async onerror callback always reads the latest value
  const fallbackSrcRef: MutableRefObject<string | undefined> =
    useRef(fallbackSrc);
  fallbackSrcRef.current = fallbackSrc;

  const hasSrc: boolean = src !== undefined && src !== "";
  const isLoaded: boolean = loadState === ImageLoadState.LOADED;
  const isError: boolean = loadState === ImageLoadState.ERROR;
  const isLoading: boolean = loadState === ImageLoadState.LOADING;
  const displaySrc: string | undefined = usingFallbackSrc ? fallbackSrc : src;
  const showImage: boolean = (hasSrc || usingFallbackSrc) && !isError;

  useEffect((): (() => void) => {
    setUsingFallbackSrc(false);

    const currentSrc: string | undefined = src;
    const currentFallbackSrc: string | undefined = fallbackSrcRef.current;
    const hasValidSrc: boolean = currentSrc !== undefined && currentSrc !== "";
    const hasValidFallback: boolean =
      currentFallbackSrc !== undefined && currentFallbackSrc !== "";

    const cleanup = (): void => {
      if (imageRef.current !== null) {
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
        imageRef.current = null;
      }
    };

    // Attempt to load fallbackSrc; on failure fall through to ERROR state
    const loadFallbackSrc = (): void => {
      if (!hasValidFallback) {
        setLoadState(ImageLoadState.ERROR);
        if (onLoadComplete !== undefined) {
          onLoadComplete(false);
        }
        return;
      }
      setUsingFallbackSrc(true);
      const fbImg: HTMLImageElement = new Image();
      imageRef.current = fbImg;
      fbImg.src = currentFallbackSrc as string;
      fbImg.onload = (): void => {
        setLoadState(ImageLoadState.LOADED);
        if (onLoadComplete !== undefined) {
          onLoadComplete(true);
        }
      };
      fbImg.onerror = (): void => {
        Log.e(
          TAG,
          "useEffect — fallback image failed to load: " +
            String(currentFallbackSrc)
        );
        setUsingFallbackSrc(false);
        setLoadState(ImageLoadState.ERROR);
        if (onLoadComplete !== undefined) {
          onLoadComplete(false);
        }
      };
    };

    if (!hasValidSrc) {
      if (hasValidFallback) {
        setLoadState(ImageLoadState.LOADING);
        loadFallbackSrc();
      } else {
        if (onLoadComplete !== undefined) {
          onLoadComplete(false);
        }
      }
    } else {
      setLoadState(ImageLoadState.LOADING);

      const img: HTMLImageElement = new Image();
      imageRef.current = img;
      img.src = currentSrc as string;

      img.onload = (): void => {
        setLoadState(ImageLoadState.LOADED);
        if (onLoadComplete !== undefined) {
          onLoadComplete(true);
        }
      };

      img.onerror = (): void => {
        Log.e(TAG, "useEffect — image failed to load: " + String(src));
        loadFallbackSrc();
      };
    }

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally re-run only when src changes
  }, [src]);

  // Memoize wrapper class to prevent recalculation on every render
  const wrapperClass: string = useMemo(
    (): string =>
      "image-loader" +
      (isLoading ? " image-loader--loading" : "") +
      (isError ? " image-loader--error" : "") +
      (classStyle !== "" ? " " + classStyle : ""),
    [isLoading, isError, classStyle]
  );

  // Memoize image class to prevent recalculation on every render
  const imageClass: string = useMemo((): string => {
    let imageModifier: string = " image-loader__image--hidden";
    if (isLoaded && disableAnimation) {
      imageModifier = " image-loader__image--visible";
    } else if (isLoaded) {
      imageModifier = " image-loader__image--fade-in";
    }
    return "image-loader__image" + imageModifier;
  }, [isLoaded, disableAnimation]);

  const showFallback: boolean = isError || (!hasSrc && !usingFallbackSrc);

  return (
    <div className={wrapperClass} data-testid="image-loader">
      {showImage && (
        <img
          src={displaySrc}
          className={imageClass}
          alt=""
          loading="lazy"
          data-testid={dataTestId}
        />
      )}
      {showFallback && fallbackText !== "" && (
        <span
          className="image-loader__fallback"
          data-testid="image-loader-fallback"
        >
          {fallbackText}
        </span>
      )}
    </div>
  );
};

/**
 * Memoized ImageLoader component to prevent unnecessary re-renders.
 * Uses shallow comparison on all props to determine if re-render is needed.
 */
const ImageLoader = memo(ImageLoaderComponent);

export default ImageLoader;
